import { getCookie, setCookie } from '@previo/core'
import { ref } from 'vue'
import router from '~/router'

export enum PreferredVersion {
  CURRENT = 'p1',
  NEW = 'p2',
}

/**
 * Shared with P1, which reads and writes the same cookie.
 */
const PREFERRED_VERSION_COOKIE_NAME = 'previo_pms_version_preference'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

export function usePreferredPMSVersion() {
  const rootDomain = window.location.hostname.split('.').slice(-2).join('.')

  const preferredPMSVersion = ref<PreferredVersion>(
    (getCookie(PREFERRED_VERSION_COOKIE_NAME) as PreferredVersion | null) ||
      PreferredVersion.CURRENT,
  )

  const writePreference = (version: PreferredVersion): void => {
    setCookie(PREFERRED_VERSION_COOKIE_NAME, version, {
      domain: rootDomain,
      path: '/',
      maxAge: COOKIE_MAX_AGE_SECONDS,
      expires: COOKIE_MAX_AGE_SECONDS,
    })
  }

  const useNew = (): void => {
    writePreference(PreferredVersion.NEW)
  }

  const useCurrent = (redirect: boolean = true): void => {
    writePreference(PreferredVersion.CURRENT)

    if (redirect) {
      router.push({
        name: router.currentRoute.value.meta.P1Route,
      })
    }
  }

  /**
   * Restarts the lifetime of an existing preference, so an active user never silently falls back to P1.
   */
  const refresh = (): void => {
    const stored = getCookie(PREFERRED_VERSION_COOKIE_NAME)

    if (stored === PreferredVersion.CURRENT || stored === PreferredVersion.NEW) {
      writePreference(stored)
    }
  }

  const usesNew = (): boolean => {
    return preferredPMSVersion.value === PreferredVersion.NEW
  }

  const usesCurrent = (): boolean => {
    return preferredPMSVersion.value === PreferredVersion.CURRENT
  }

  return {
    useNew,
    useCurrent,
    refresh,
    usesNew,
    usesCurrent,
  }
}
